// external imports
import { InMemoryCache, NormalizedCacheObject } from 'apollo-cache-inmemory';
import { ApolloClient } from 'apollo-client';
import { setContext } from 'apollo-link-context';
import { createHttpLink } from 'apollo-link-http';
import gql from 'graphql-tag';

// internal imports
import ConfigUtil from 'core/util/config/ConfigUtil';
import TokenSession from 'core/util/session/TokenSession';
import StringUtil from 'core/util/StringUtil';
import Service from "../../../decorators/Service";
import IResolverService, { QueryFunction } from '../IResolverService';

/**
 * @overview: An {@link IQueryResolver} using the react-apollo graphql client.
 * @author: jesposito
 */

@Service()
export default class ApolloResolverService extends IResolverService {
	private static NAME: string = "apollo";
	private client: ApolloClient<NormalizedCacheObject>;

	constructor() {
		super();

		// create an Apollo http link with out endpoint url
		const httpLink = createHttpLink({
			uri: ConfigUtil.getEnvConfig().graphqlUri
		});

		// create query context that includes our bearer token
		const authLink = setContext((_, { headers }) => {

			// get the authentication token from local storage if it exists
			const token = TokenSession.getToken();

			// return the headers to the context so httpLink can read them
			return {
				headers: {
					...headers,
					authorization: token ? `Bearer ${token}` : StringUtil.EMPTY,
				}
			};
		});

		const cache = new InMemoryCache();

		// create the apollo client
		this.client = new ApolloClient({
			link: authLink.concat(httpLink),
			cache
		});
	}

	public execute(query: string | QueryFunction, params?: object): Promise<any> {
		const q = typeof query === "string" ? query : query(params);

		if(q.trim().startsWith("mutation")) {
			return this.client.mutate({mutation: gql`${query}`});
		}

		return this.client.query({query: gql`${query}`});
	}

	public getServiceId(): string {
		return ApolloResolverService.NAME;
	}

	public clearCache() {
		return this.client.resetStore();
	}
}



// WEBPACK FOOTER //
// ./src/core/service/resolver/impl/ApolloResolver.service.ts